/**
 * Pick existing documents from the Documents system to attach to a message.
 *
 * On a member DM the picker starts on that member's documents
 * (GET /api/documents/member/{id}, filtered client-side) with a toggle to
 * search all organization documents; everywhere else it uses the org-wide
 * search (GET /api/documents/search, which matches file names and document
 * type names). Each document's current file is what gets attached — the caller
 * turns a selection into attachment ids via POST /api/messaging/messages/attach-document.
 *
 * Failures show the server's own detail with a Retry, never a bare
 * "could not load", so a broken endpoint is diagnosable from the screen.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, FileText, Loader2, RefreshCw, Search } from 'lucide-react';
import { messagingApi, PickerDocument, PickerDocumentFile } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '@/hooks/useUserTimezone';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { cn } from '@/lib/utils';
import { errorDetailText } from './errorDetail';
import { formatBytes } from './cloud/CloudIcons';

export interface SelectedDocument {
  docRecordId: number;
  docFileId: number | null;
  name: string;
}

export function currentFile(doc: PickerDocument): PickerDocumentFile | undefined {
  return doc.files?.find((f) => f.is_current) || doc.files?.[0];
}

export function documentDisplayName(doc: PickerDocument): string {
  return currentFile(doc)?.original_filename || doc.doc_type_name || `#${doc.id}`;
}

function hasFile(doc: PickerDocument): boolean {
  return (doc.files?.length ?? 0) > 0 || (doc.file_count ?? 0) > 0;
}

interface DocumentPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Member DM → that member's documents first; otherwise org-wide search. */
  memberId?: number | null;
  onConfirm: (docs: SelectedDocument[]) => void;
  maxSelection?: number;
}

export function DocumentPickerDialog({
  open,
  onOpenChange,
  memberId,
  onConfirm,
  maxSelection = 5,
}: DocumentPickerDialogProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [allOrg, setAllOrg] = useState(false);
  const [selected, setSelected] = useState<Record<number, SelectedDocument>>({});

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSelected({});
      setSearch('');
      setDebounced('');
      setAllOrg(false);
    }
  }, [open]);

  const memberScope = !!memberId && !allOrg;

  const { data: docs = [], isLoading, isFetching, error, refetch } = useQuery({
    // Member lists are filtered client-side, so the search term is only part
    // of the key for the org-wide search.
    queryKey: ['message-doc-picker', memberScope ? memberId : null, memberScope ? '' : debounced],
    queryFn: () =>
      messagingApi.listDocumentsForPicker({
        memberId: memberScope ? memberId : null,
        query: memberScope ? undefined : debounced,
      }),
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });

  const visible = useMemo(() => {
    if (!memberScope || !debounced) return docs;
    const q = debounced.toLowerCase();
    return docs.filter((d) =>
      [documentDisplayName(d), d.doc_type_name, d.doc_subtype_name, d.category_name, d.program_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [docs, memberScope, debounced]);

  const selectedCount = Object.keys(selected).length;

  const toggle = (doc: PickerDocument) => {
    if (!hasFile(doc)) return;
    setSelected((prev) => {
      if (prev[doc.id]) {
        const next = { ...prev };
        delete next[doc.id];
        return next;
      }
      if (Object.keys(prev).length >= maxSelection) return prev;
      return {
        ...prev,
        [doc.id]: { docRecordId: doc.id, docFileId: currentFile(doc)?.id ?? null, name: documentDisplayName(doc) },
      };
    });
  };

  const errorDetail = error ? errorDetailText(error) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('messaging.documents.pickerTitle', 'Attach documents')}</DialogTitle>
          <DialogDescription>
            {memberScope
              ? t('messaging.documents.pickerMemberHint', "Showing this member's documents.")
              : t('messaging.documents.pickerOrgHint', "Search your organization's documents.")}
          </DialogDescription>
        </DialogHeader>

        {memberId ? (
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>{t('messaging.documents.allOrgToggle', 'All organization documents')}</span>
            <Switch
              checked={allOrg}
              onCheckedChange={(v: boolean) => setAllOrg(!!v)}
              aria-label={t('messaging.documents.allOrgToggle', 'All organization documents')}
            />
          </label>
        ) : null}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('messaging.documents.searchPlaceholder', 'Search documents')}
            aria-label={t('messaging.documents.searchPlaceholder', 'Search documents')}
            className="pl-9"
          />
          {isFetching && !isLoading && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>

        <div className="max-h-80 min-h-[8rem] space-y-1 overflow-y-auto">
          {isLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && error && (
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm" role="alert">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="font-medium text-destructive">
                {t('messaging.documents.loadFailed', 'Could not load documents.')}
              </p>
              {errorDetail && (
                <p className="max-w-sm break-words text-xs text-muted-foreground" data-testid="doc-picker-error-detail">
                  {errorDetail}
                </p>
              )}
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                {t('messaging.common.retry', 'Retry')}
              </Button>
            </div>
          )}
          {!isLoading && !error && visible.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {debounced
                ? t('messaging.documents.noMatches', 'No documents match "{{q}}".', { q: debounced })
                : memberScope
                  ? t('messaging.documents.emptyMember', 'This member has no documents yet.')
                  : t('messaging.documents.empty', 'No documents found.')}
            </div>
          )}
          {!isLoading &&
            !error &&
            visible.map((doc) => {
              const isSelected = !!selected[doc.id];
              const file = currentFile(doc);
              const disabled = !hasFile(doc);
              const typeLabel = [doc.doc_type_name, doc.doc_subtype_name].filter(Boolean).join(' › ');
              const meta = [
                typeLabel || null,
                memberScope ? null : doc.target_name,
                formatBytes(file?.file_size_bytes),
                doc.created_at ? fmt.medium(doc.created_at) : null,
              ].filter(Boolean);
              return (
                <button
                  key={doc.id}
                  type="button"
                  data-testid="doc-picker-row"
                  onClick={() => toggle(doc)}
                  disabled={disabled}
                  aria-pressed={isSelected}
                  title={disabled ? t('messaging.documents.noFile', 'No file uploaded for this document') : undefined}
                  className={cn(
                    'flex w-full items-center gap-3 rounded px-2 py-2 text-left hover:bg-muted/60',
                    isSelected && 'bg-primary/10',
                    disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                  )}
                >
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{documentDisplayName(doc)}</div>
                    <div className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</div>
                    {disabled && (
                      <div className="text-xs text-amber-700 dark:text-amber-400">
                        {t('messaging.documents.noFile', 'No file uploaded for this document')}
                      </div>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                </button>
              );
            })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('messaging.channel.cancel', 'Cancel')}
          </Button>
          <Button
            disabled={selectedCount === 0}
            onClick={() => {
              onConfirm(Object.values(selected));
              onOpenChange(false);
            }}
          >
            {t('messaging.documents.attachCount', 'Attach ({{n}})', { n: selectedCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPickerDialog;
